/**
 * Collection PLP compare: checkboxes on cards, sticky bar, and a theme drawer (`#collection-compare`)
 * showing a CSS grid of specs. Payload per product comes from JSON `<script id="collection-compare-data-{id}">`
 * rendered in `collection-compare-product-embed.liquid`. Compare rows use keys inside `technical_spec.General`.
 * Drawer opens via theme `openPanel('collection-compare')`
 * (`custom-async.js`), which may load after this file — we poll briefly if needed.
 */
(() => {
  // ---------------------------------------------------------------------------
  // Configuration (keep in sync with Liquid / locales)
  // ---------------------------------------------------------------------------

  /** Theme panel id — must match `collection-compare-drawer.liquid` and `openPanel` argument. */
  const DRAWER_PANEL_ID = "collection-compare";

  const DEFAULT_MAX_PRODUCTS = 4;

  /** Compare drawer is only useful with at least two products. */
  const MIN_COMPARE_PRODUCTS = 2;

  /**
   * Row keys for the spec grid are taken from each product’s
   * `technical_spec` object.
   */

  /**
   * Extra dimension rows after `technical_spec` rows. Each entry is
   * `[fieldOnProductJson, translationKeyUnderCollectionCompareRow]`.
   * Field names match keys emitted by `collection-compare-product-embed.liquid`.
   */
  const DIMENSION_FIELD_ROWS = [
    ["boxed_weight", "boxed_weight"],
    ["boxed_height", "boxed_height"],
    ["boxed_width", "boxed_width"],
    ["boxed_depth", "boxed_depth"],
    ["unboxed_width", "unboxed_width"],
    ["unboxed_weight", "unboxed_weight"],
    ["unboxed_height", "unboxed_height"],
    ["unboxed_depth", "unboxed_depth"],
  ];

  // ---------------------------------------------------------------------------
  // i18n (injected by `collection-compare-modal.liquid` → `#collection-compare-i18n`)
  // ---------------------------------------------------------------------------

  function readCompareStrings() {
    try {
      const el = document.getElementById("collection-compare-i18n");
      if (el && el.textContent) return JSON.parse(el.textContent.trim());
    } catch {
      /* ignore */
    }
    return {};
  }

  const strings = readCompareStrings();

  // ---------------------------------------------------------------------------
  // DOM references (re-query after `shopify:section:load` — bar lives in collection section)
  // ---------------------------------------------------------------------------

  const els = {
    bar: null,
    drawer: null,
    gridMount: null,
    chips: null,
    message: null,
    openButton: null,
  };

  function refreshCompareDomRefs() {
    els.bar = document.getElementById("collection-compare-bar");
    els.drawer = document.getElementById("collection-compare");
    els.gridMount = document.getElementById("collection-compare-modal-body");
    els.chips = document.getElementById("collection-compare-bar-chips");
    els.message = document.getElementById("collection-compare-bar-msg");
    els.openButton = document.getElementById("collection-compare-open");
    return Boolean(
      els.bar && els.drawer && els.gridMount && els.openButton && els.chips,
    );
  }

  let maxProducts = DEFAULT_MAX_PRODUCTS;

  function readMaxProductsFromBar() {
    maxProducts = DEFAULT_MAX_PRODUCTS;
    const maxAttr = els.bar?.dataset?.maxProducts;
    if (maxAttr) {
      const parsed = Number.parseInt(maxAttr, 10);
      if (!Number.isNaN(parsed) && parsed > 0) maxProducts = parsed;
    }
  }

  if (!refreshCompareDomRefs()) {
    return;
  }
  readMaxProductsFromBar();

  /** Product ids selected for compare, in pick order. */
  let selectedProductIds = [];
  let compareDomObserver = null;

  // ---------------------------------------------------------------------------
  // Product JSON (one script tag per product on the collection grid)
  // ---------------------------------------------------------------------------

  function parseProductPayload(productId) {
    const script = document.getElementById(
      `collection-compare-data-${productId}`,
    );
    if (!script || !script.textContent) return null;
    try {
      return JSON.parse(script.textContent.trim());
    } catch {
      return null;
    }
  }

  function selectedProductsInOrder() {
    return selectedProductIds
      .map((id) => parseProductPayload(id))
      .filter(Boolean);
  }

  /** Shopify default product placeholder (`placeholder_svg_tag` in `collection-compare-modal.liquid`). */
  let compareProductPlaceholderHtml = null;
  function getCompareProductPlaceholderHtml() {
    if (compareProductPlaceholderHtml !== null)
      return compareProductPlaceholderHtml;
    const tpl = document.getElementById(
      "collection-compare-product-placeholder-svg",
    );
    compareProductPlaceholderHtml = tpl
      ? tpl.innerHTML.trim()
      : '<span class="collection-compare-chip__ph" aria-hidden="true"></span>';
    return compareProductPlaceholderHtml;
  }

  function productImageSrc(product) {
    return product.image != null ? String(product.image).trim() : "";
  }

  // ---------------------------------------------------------------------------
  // Safe HTML / cell display
  // ---------------------------------------------------------------------------

  function escapeHtml(str) {
    if (str == null) return "";
    const div = document.createElement("div");
    div.textContent = String(str);
    return div.innerHTML;
  }

  const ESCAPE_ATTR_MAP = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };

  function escapeAttr(str) {
    return String(str == null ? "" : str).replace(
      /[&<>"']/g,
      (ch) => ESCAPE_ATTR_MAP[ch],
    );
  }

  /** Empty → em dash; objects JSON-stringified (escaped). */
  function formatGridCell(value) {
    if (value == null || value === "") return "—";
    if (typeof value === "object") return escapeHtml(JSON.stringify(value));
    return escapeHtml(String(value));
  }

  // ---------------------------------------------------------------------------
  // `technical_spec` metafield — row keys come from `technical_spec.General` only
  // ---------------------------------------------------------------------------

  function getTechnicalSpecObject(technicalSpec) {
    if (!technicalSpec) return null;
    if (typeof technicalSpec === "string") {
      try {
        return JSON.parse(technicalSpec);
      } catch (err) {
        console.error("Error parsing technicalSpec JSON string:", err, technicalSpec);
        return null;
      }
    }
    if (typeof technicalSpec === "object" && !Array.isArray(technicalSpec)) {
      return technicalSpec;
    }
    return null;
  }

  /** Common keys inside each product’s `technical_spec`, ordered by first product’s keys. */
  function collectTechnicalSpecKeysAcrossProducts(products) {
    if (!products || products.length === 0) return [];

    const firstProduct = products[0];
    const firstSpec = getTechnicalSpecObject(firstProduct.technical_spec);
    if (!firstSpec) return [];

    const candidateKeys = Object.keys(firstSpec);

    return candidateKeys.filter((key) => {
      for (let i = 0; i < products.length; i++) {
        const spec = getTechnicalSpecObject(products[i].technical_spec);
        if (!spec || !Object.prototype.hasOwnProperty.call(spec, key)) {
          return false;
        }
      }
      return true;
    });
  }

  function readTechnicalSpecValue(product, key) {
    const spec = getTechnicalSpecObject(product.technical_spec);
    if (!spec) return "";
    if (!Object.prototype.hasOwnProperty.call(spec, key)) return "";
    return spec[key];
  }

  function translateDimensionRow(i18nKey) {
    return strings[i18nKey] || i18nKey;
  }

  // ---------------------------------------------------------------------------
  // Compare grid markup (`collection-compare-modal.css` — CSS Grid, not <table>)
  // ---------------------------------------------------------------------------

  function gridTemplateColumnsStyle(productCount) {
    return `minmax(10rem, 1fr) repeat(${productCount}, minmax(12rem, 1fr))`;
  }

  function buildHeaderCellsHtml(products) {
    const viewDetailsLabel = escapeHtml(strings.view_details || "View details");
    let html =
      '<div role="columnheader" class="collection-compare-grid__cell collection-compare-grid__cell--corner"></div>';
    for (const product of products) {
      html +=
        '<div role="columnheader" class="collection-compare-grid__cell collection-compare-grid__cell--product">';
      html += '<div class="collection-compare-grid__product-card">';
      const headerImageSrc = productImageSrc(product);
      html += '<div class="collection-compare-grid__product-media">';
      if (headerImageSrc) {
        html += `<img src="${escapeAttr(headerImageSrc)}" alt="" width="200" height="200" loading="lazy">`;
      } else {
        html += getCompareProductPlaceholderHtml();
      }
      html += "</div>";
      html += `<h4 class="collection-compare-grid__product-title" title="${escapeAttr(
        String(product.title || ""),
      )}">${escapeHtml(product.title)}</h4>`;
      html += '<div class="collection-compare-grid__product-footer">';
      if (product.compare_at_price || product.price) {
        html += '<p class="collection-compare-grid__product-price">';
        if (product.compare_at_price) {
          html += `<span class="old-price">${escapeHtml(
            String(product.compare_at_price),
          )}</span>&nbsp;`;
        }
        if (product.price) {
          html += escapeHtml(String(product.price));
        }
        html += "</p>";
      }
      const url = escapeAttr(product.url || "#");
      html += `<p class="link-btn collection-compare-grid__product-actions"><a href="${url}" class="overlay-primary overlay-buy_button">${viewDetailsLabel}</a></p>`;
      html += "</div></div></div>";
    }
    return html;
  }

  /**
   * One logical grid row: label column + one cell per product.
   * Wrapper uses `display: contents` so children participate in the parent grid.
   */
  function buildGridRowHtml(labelText, products, getValueForProduct) {
    let html =
      '<div class="collection-compare-grid__row" role="row" style="display: contents;">';
    html += `<div role="rowheader" class="collection-compare-grid__cell collection-compare-grid__cell--label">${escapeHtml(
      labelText,
    )}</div>`;
    for (const product of products) {
      html += `<div role="cell" class="collection-compare-grid__cell">${formatGridCell(
        getValueForProduct(product),
      )}</div>`;
    }
    html += "</div>";
    return html;
  }

  function renderCompareGrid(products) {
    if (!els.gridMount) return;
    const specKeys = collectTechnicalSpecKeysAcrossProducts(products);
    const colTemplate = gridTemplateColumnsStyle(products.length);

    let html = `<div class="collection-compare-drawer__scroll-x"><div class="collection-compare-grid" role="table" data-compare-products="${products.length}" style="grid-template-columns: ${colTemplate};">`;

    html += `<div class="collection-compare-grid__row collection-compare-grid__row--head collection-compare-grid__head-sticky" role="row">`;
    html += buildHeaderCellsHtml(products);
    html += "</div>";

    for (const key of specKeys) {
      html += buildGridRowHtml(key, products, (product) =>
        readTechnicalSpecValue(product, key),
      );
    }

    for (const [jsonField, i18nKey] of DIMENSION_FIELD_ROWS) {
      const label = translateDimensionRow(i18nKey);
      html += buildGridRowHtml(
        label,
        products,
        (product) => product[jsonField],
      );
    }

    html += "</div></div>";
    els.gridMount.innerHTML = html;
  }

  // ---------------------------------------------------------------------------
  // Sticky bar UI
  // ---------------------------------------------------------------------------

  function flashBarMessage(text) {
    if (els.message) els.message.textContent = text || "";
    if (!text) return;
    window.setTimeout(() => {
      if (els.message) els.message.textContent = "";
    }, 4000);
  }

  function setCheckboxCheckedForProduct(productId, checked) {
    document
      .querySelectorAll(
        `.collection-compare-checkbox[data-compare-product-id="${productId}"]`,
      )
      .forEach((input) => {
        input.checked = checked;
      });
  }

  function renderChipsInBar() {
    if (!els.chips) return;
    els.chips.innerHTML = "";
    for (const id of selectedProductIds) {
      const product = parseProductPayload(id);
      if (!product) continue;

      const chip = document.createElement("div");
      chip.className = "collection-compare-chip";
      chip.setAttribute("data-compare-product-id", String(id));

      const imageSrc = productImageSrc(product);
      const thumb = imageSrc
        ? `<img src="${escapeAttr(imageSrc)}" alt="" width="100" height="100" loading="lazy">`
        : getCompareProductPlaceholderHtml();
      const removeLabel = escapeHtml(strings.remove || "Remove");
      chip.innerHTML = `<div class="collection-compare-chip__body">${thumb}<span class="collection-compare-chip__title">${escapeHtml(
        product.title,
      )}</span></div><a href="#" class="collection-compare-chip__remove" aria-label="${removeLabel}" data-compare-product-id="${escapeAttr(
        String(id),
      )}"></a>`;
      els.chips.appendChild(chip);
    }
  }

  function syncEveryCheckboxWithSelection() {
    const selected = Object.create(null);
    for (const id of selectedProductIds) selected[String(id)] = true;

    document
      .querySelectorAll(".collection-compare-checkbox")
      .forEach((input) => {
        const id = String(input.getAttribute("data-compare-product-id") || "");
        input.checked = Boolean(selected[id]);
      });
  }

  function refreshStickyBar() {
    if (!els.bar || !els.chips || !els.openButton) return;

    const products = selectedProductsInOrder();
    selectedProductIds = products.map((p) => String(p.id));
    const count = products.length;

    els.bar.hidden = count === 0;
    document.documentElement.classList.toggle(
      "collection-compare-bar-visible",
      count > 0,
    );
    els.openButton.disabled = count < MIN_COMPARE_PRODUCTS;
    syncEveryCheckboxWithSelection();
    renderChipsInBar();
  }

  function removeProductFromSelection(productId) {
    const id = String(productId);
    selectedProductIds = selectedProductIds.filter((x) => String(x) !== id);
    setCheckboxCheckedForProduct(id, false);
    refreshStickyBar();
  }

  function clearAllCompareSelection() {
    selectedProductIds = [];
    syncEveryCheckboxWithSelection();
    refreshStickyBar();
  }

  // ---------------------------------------------------------------------------
  // Drawer (theme `.m6pn` panel)
  // ---------------------------------------------------------------------------

  function openDrawerWhenReady() {
    const callOpenPanel = () => {
      if (typeof globalThis.openPanel !== "function") return false;
      globalThis.openPanel(DRAWER_PANEL_ID);
      return true;
    };

    if (callOpenPanel()) return;

    let tries = 0;
    const maxTries = 80;
    const timer = window.setInterval(() => {
      tries += 1;
      if (callOpenPanel() || tries >= maxTries) window.clearInterval(timer);
    }, 50);
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  function onDocumentChange(event) {
    const target = event.target;
    if (
      !target ||
      !target.classList ||
      !target.classList.contains("collection-compare-checkbox")
    ) {
      return;
    }

    const productId = String(
      target.getAttribute("data-compare-product-id") || "",
    );
    if (!productId) return;

    if (target.checked) {
      if (selectedProductIds.length >= maxProducts) {
        target.checked = false;
        flashBarMessage(
          strings.max_products || "You can compare up to 4 products.",
        );
        return;
      }
      if (!selectedProductIds.includes(productId))
        selectedProductIds.push(productId);
    } else {
      selectedProductIds = selectedProductIds.filter(
        (x) => String(x) !== productId,
      );
    }
    refreshStickyBar();
  }

  function onOpenCompareClick() {
    const products = selectedProductsInOrder();
    if (products.length < MIN_COMPARE_PRODUCTS) {
      flashBarMessage(
        strings.select_min_two ||
          "Select at least two products to compare.",
      );
      return;
    }
    renderCompareGrid(products);
    openDrawerWhenReady();
  }

  function onSectionLoad(event) {
    syncEveryCheckboxWithSelection();

    const root = event.target;
    if (!(root instanceof Element) || !root.querySelector("#collection-compare-bar")) {
      return;
    }

    if (!refreshCompareDomRefs()) {
      return;
    }
    readMaxProductsFromBar();
    refreshStickyBar();
  }

  /**
   * Theme `.l4cl a:after` can steal hits over the compare row (target = PDP `<a>` while the
   * pointer is over `.collection-compare-pick`). If coordinates lie on the pick row, treat as
   * compare: block navigation and toggle the checkbox (same outcome as a real label hit).
   */
  function onComparePickGhostLinkClick(event) {
    if (event.type !== "click" || event.defaultPrevented) return;
    if (event.button !== 0) return;

    const card = event.target.closest(".l4cl li.product-card");
    if (!card) return;

    const pick = card.querySelector(".collection-compare-pick");
    if (!pick) return;

    if (event.target.closest(".collection-compare-pick")) {
      event.stopPropagation();
      return;
    }

    const rect = pick.getBoundingClientRect();
    const pad = 3;
    const { clientX: x, clientY: y } = event;
    const overPick =
      x >= rect.left - pad &&
      x <= rect.right + pad &&
      y >= rect.top - pad &&
      y <= rect.bottom + pad;
    if (!overPick) return;

    const link = event.target.closest("a[href]");
    if (!link || !card.contains(link)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const cb = pick.querySelector(
      "input.collection-compare-checkbox[type='checkbox']",
    );
    if (!cb || cb.disabled) return;

    cb.checked = !cb.checked;
    cb.dispatchEvent(new Event("input", { bubbles: true }));
    cb.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function onDocumentClick(event) {
    const openButton = event.target.closest(".collection-compare-bar__open");
    if (openButton) {
      event.preventDefault();
      onOpenCompareClick();
      return;
    }

    const clearAll = event.target.closest(".collection-compare-bar__clear");
    if (clearAll) {
      event.preventDefault();
      clearAllCompareSelection();
      return;
    }

    const remove = event.target.closest(".collection-compare-chip__remove");
    if (remove) {
      event.preventDefault();
      const id = remove.getAttribute("data-compare-product-id");
      if (id) removeProductFromSelection(id);
      return;
    }
  }

  function onCompareDomMutation(mutations) {
    let compareMarkupAdded = false;
    for (const mutation of mutations) {
      if (!mutation.addedNodes.length) continue;
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (
          node.matches?.(".collection-compare-checkbox, .collection-compare-bar, .collection-compare-open, .collection-compare-chip__remove") ||
          node.querySelector?.(".collection-compare-checkbox, .collection-compare-bar, .collection-compare-open, .collection-compare-chip__remove")
        ) {
          compareMarkupAdded = true;
          break;
        }
      }
      if (compareMarkupAdded) break;
    }

    if (!compareMarkupAdded) return;

    if (refreshCompareDomRefs()) {
      readMaxProductsFromBar();
      unbindBarEvents();
      bindBarEvents();
    }
    syncEveryCheckboxWithSelection();
  }

  function startCompareDomObserver() {
    if (compareDomObserver) return;
    compareDomObserver = new MutationObserver(onCompareDomMutation);
    compareDomObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function bindEvents() {
    document.addEventListener("change", onDocumentChange);
    document.addEventListener("click", onComparePickGhostLinkClick, true);
    document.addEventListener("click", onDocumentClick);
    document.addEventListener("shopify:section:load", onSectionLoad);
    startCompareDomObserver();
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  bindEvents();
  refreshStickyBar();
})();
